import type { DemoViewerRecord } from "./busyClosesDemo.types";
import type { DemoRandom } from "./demoRandom";
import { type DemoPerson, FALLBACK_PEOPLE } from "./demoSources";

/** Every third fake item goes to the signed-in user; every third (offset by one) starts with viewers. */
const SELF_EVERY = 3;
const VIEWED_OFFSET = 1;

export interface DemoTeam {
    self: DemoPerson | null;
    /** Teammates other than the signed-in user; the generic people when there are none. */
    others: readonly DemoPerson[];
    /** Real teammates who can show as viewers. */
    viewerCandidateIds: readonly number[];
}

/** The team the fakes draw on: the real members, or generic people when there are none. */
export const getDemoTeam = ({
    people,
    selfId,
}: {
    people: readonly DemoPerson[];
    selfId: number | null;
}): DemoTeam => {
    const others = people.filter((person) => person.id !== selfId);
    return {
        self: people.find((person) => person.id === selfId) ?? null,
        others: others.length > 0 ? others : FALLBACK_PEOPLE,
        viewerCandidateIds: others.map((person) => person.id),
    };
};

/** The `index`th fake item's lead: the signed-in user on every third, else a seeded teammate. */
export const pickDemoLead = ({
    team,
    random,
    index,
}: {
    team: DemoTeam;
    random: DemoRandom;
    index: number;
}): DemoPerson =>
    index % SELF_EVERY === 0 && team.self !== null
        ? team.self
        : random.pick(team.others);

/** A fake item's opening viewers (one or two on about a third of items) and who may join later. */
export const getDemoViewerRecord = ({
    team,
    random,
    index,
}: {
    team: DemoTeam;
    random: DemoRandom;
    index: number;
}): DemoViewerRecord => ({
    viewerIds:
        index % SELF_EVERY === VIEWED_OFFSET
            ? random.shuffle(team.viewerCandidateIds).slice(0, random.int(1, 2))
            : [],
    candidateIds: team.viewerCandidateIds,
});
